import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../components/layout/MainLayout";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Notifications.css";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
];

const TYPE_CONFIG = {
  leave: {
    icon: "fa-calendar-times",
    label: "Leave",
    color: "#d97706",
    bg: "#fef3c7",
  },
  payroll: {
    icon: "fa-money-bill-wave",
    label: "Payroll",
    color: "#059669",
    bg: "#d1fae5",
  },
  attendance: {
    icon: "fa-fingerprint",
    label: "Attendance",
    color: "#2563eb",
    bg: "#dbeafe",
  },
  ticket: {
    icon: "fa-ticket-alt",
    label: "Ticket",
    color: "#7c3aed",
    bg: "#ede9fe",
  },
  alert: {
    icon: "fa-exclamation-circle",
    label: "Alert",
    color: "#dc2626",
    bg: "#fee2e2",
  },
  info: {
    icon: "fa-info-circle",
    label: "Info",
    color: "#0284c7",
    bg: "#e0f2fe",
  },
  success: {
    icon: "fa-check-circle",
    label: "Success",
    color: "#16a34a",
    bg: "#dcfce7",
  },
};

function Notifications() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState("all");
  const [unreadCount, setUnreadCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/notifications", {
        params: { filter, page, per_page: 15 },
      });
      if (response.data.success) {
        setNotifications(response.data.data);
        setUnreadCount(response.data.unread_count);
        setTotal(response.data.total);
        setLastPage(response.data.last_page);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load notifications",
      });
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleFilterChange = (key) => {
    setFilter(key);
    setPage(1);
  };

  const handleMarkRead = async (id) => {
    setActionLoading(id);
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      // silent
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      Swal.fire({
        icon: "success",
        title: "Done!",
        text: "All notifications marked as read.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to mark all as read",
      });
    }
  };

  const handleDelete = async (id) => {
    setActionLoading(`del-${id}`);
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setTotal((prev) => prev - 1);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete notification",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearAll = () => {
    Swal.fire({
      title: "Clear All Notifications?",
      text: "This will permanently delete all your notifications.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Clear All",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete("/notifications");
          setNotifications([]);
          setUnreadCount(0);
          setTotal(0);
          Swal.fire({
            icon: "success",
            title: "Cleared!",
            text: "All notifications have been removed.",
            timer: 1500,
            showConfirmButton: false,
          });
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to clear notifications",
          });
        }
      }
    });
  };

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) await handleMarkRead(notif.id);
    if (notif.url) navigate(notif.url);
  };

  const getTypeConfig = (type) => TYPE_CONFIG[type] || TYPE_CONFIG["info"];

  const renderPagination = () => {
    if (lastPage <= 1) return null;
    return (
      <div className="notif-page-pagination">
        <span className="notif-page-info">
          Page {page} of {lastPage}
        </span>
        <div className="notif-page-btns">
          <button
            className="notif-pg-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <i className="fas fa-chevron-left"></i> Prev
          </button>
          <button
            className="notif-pg-btn"
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            disabled={page === lastPage}
          >
            Next <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="notif-page-container">
        {/* ── Page Header ── */}
        <div className="notif-page-header">
          <div className="notif-page-title-row">
            <div>
              <h4 className="notif-page-title">
                <i className="fas fa-bell"></i>
                Notifications
              </h4>
              <p className="notif-page-subtitle">
                {total} total &nbsp;·&nbsp;
                <span className="unread-highlight">{unreadCount} unread</span>
              </p>
            </div>
            <div className="notif-page-actions">
              {unreadCount > 0 && (
                <button
                  className="notif-action-btn outline"
                  onClick={handleMarkAllRead}
                >
                  <i className="fas fa-check-double"></i>
                  Mark All Read
                </button>
              )}
              {total > 0 && (
                <button
                  className="notif-action-btn danger"
                  onClick={handleClearAll}
                >
                  <i className="fas fa-trash-alt"></i>
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="notif-filter-tabs">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                className={`notif-filter-tab ${filter === f.key ? "active" : ""}`}
                onClick={() => handleFilterChange(f.key)}
              >
                {f.label}
                {f.key === "unread" && unreadCount > 0 && (
                  <span className="tab-badge">{unreadCount}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Main Card ── */}
        <div className="notif-page-card">
          {loading ? (
            <div className="notif-page-loading">
              <div className="notif-page-spinner"></div>
              <p>Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="notif-page-empty">
              <div className="notif-empty-icon-wrap">
                <i className="fas fa-bell-slash"></i>
              </div>
              <h5>
                {filter === "unread"
                  ? "No unread notifications"
                  : filter === "read"
                    ? "No read notifications"
                    : "No notifications yet"}
              </h5>
              <p>
                {filter !== "all"
                  ? 'Try switching to "All" to see everything.'
                  : "You're all caught up!"}
              </p>
              {filter !== "all" && (
                <button
                  className="notif-action-btn outline"
                  onClick={() => handleFilterChange("all")}
                >
                  View All
                </button>
              )}
            </div>
          ) : (
            <>
              {notifications.map((notif, idx) => {
                const cfg = getTypeConfig(notif.type);
                const isDeleting = actionLoading === `del-${notif.id}`;
                const isMarking = actionLoading === notif.id;

                return (
                  <div
                    key={notif.id}
                    className={`notif-page-item ${!notif.is_read ? "unread" : ""} ${isDeleting ? "deleting" : ""}`}
                  >
                    {/* Unread Strip */}
                    {!notif.is_read && <div className="unread-strip"></div>}

                    {/* Icon */}
                    <div
                      className="notif-page-icon"
                      style={{ background: cfg.bg, color: cfg.color }}
                    >
                      <i className={`fas ${cfg.icon}`}></i>
                    </div>

                    {/* Content */}
                    <div
                      className="notif-page-content"
                      onClick={() => handleNotifClick(notif)}
                    >
                      <div className="notif-page-top">
                        <span
                          className="notif-type-tag"
                          style={{ background: cfg.bg, color: cfg.color }}
                        >
                          {cfg.label}
                        </span>
                        {!notif.is_read && (
                          <span className="new-badge">NEW</span>
                        )}
                      </div>
                      <p className="notif-page-title-text">{notif.title}</p>
                      <p className="notif-page-message">{notif.message}</p>
                      <div className="notif-page-meta">
                        <span className="notif-time-ago">
                          <i className="fas fa-clock"></i>
                          {notif.time_ago}
                        </span>
                        <span className="notif-full-date">
                          {notif.created_at}
                        </span>
                        {notif.is_read && notif.read_at && (
                          <span className="notif-read-at">
                            <i className="fas fa-check-double"></i>
                            Read {notif.read_at}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="notif-page-item-actions">
                      {!notif.is_read && (
                        <button
                          className="notif-item-btn mark-read"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkRead(notif.id);
                          }}
                          disabled={isMarking}
                          title="Mark as read"
                        >
                          {isMarking ? (
                            <span className="mini-spinner"></span>
                          ) : (
                            <i className="fas fa-check"></i>
                          )}
                        </button>
                      )}
                      <button
                        className="notif-item-btn delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(notif.id);
                        }}
                        disabled={isDeleting}
                        title="Delete notification"
                      >
                        {isDeleting ? (
                          <span className="mini-spinner red"></span>
                        ) : (
                          <i className="fas fa-times"></i>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}

              {renderPagination()}
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
}

export default Notifications;
